// =============================================================================
// GAME STORE - Zustand store for game state management
// =============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  GameState,
  GameScenario,
  Option,
  Message,
  DecisionRecord,
  DonationCampaign,
  GameOverType,
} from '../types';
import { INITIAL_PARAMETERS, MAX_DECISIONS } from '../types';
import { STORAGE_KEYS, SESSION_KEYS } from '../constants';

// =============================================================================
// TYPES
// =============================================================================

type ParameterValues = Pick<
  GameState,
  'militaryStrength' | 'economy' | 'resilience' | 'westernSupport' | 'trust'
>;

interface GameStoreState {
  // Core game state
  gameState: GameState;

  // Additional state not in GameState interface
  endingSummary: string;
  consequences: string | undefined;
  maxDecisions: number;
  streamText: string;

  // UI state
  showActionStrip: boolean;
  actionStripShown: boolean;

  // Campaign
  donationCampaign: DonationCampaign | null;
}

interface GameStoreActions {
  // Game initialization
  initializeGame: (
    scenario: GameScenario,
    config: { maxDecisions: number; initialParameters: ParameterValues },
  ) => void;

  // Game state mutations
  updateParameters: (params: Partial<ParameterValues>) => void;
  setCurrentScenario: (scenario: GameScenario | null) => void;
  incrementDecision: () => void;
  setPhase: (phase: GameState['phase']) => void;
  setGameOver: (type: GameOverType) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Decision handling
  addDecisionRecord: (record: DecisionRecord) => void;
  addToConversationHistory: (messages: Message[]) => void;

  // Streaming
  setStreamText: (text: string) => void;
  appendStreamText: (delta: string) => void;
  setEndingSummary: (summary: string) => void;
  appendEndingSummary: (delta: string) => void;
  setConsequences: (consequences: string | undefined) => void;

  // Action Strip
  setShowActionStrip: (show: boolean) => void;
  markActionStripShown: () => void;

  // Campaign
  setDonationCampaign: (campaign: DonationCampaign | null) => void;

  // Reset
  resetGame: () => void;
  clearProgress: () => void;
}

export type GameStore = GameStoreState & GameStoreActions;

// =============================================================================
// INITIAL STATE
// =============================================================================

const createInitialGameState = (): GameState => ({
  phase: 'playing',
  decisionNumber: 1,
  currentScenario: null,
  ...INITIAL_PARAMETERS,
  conversationHistory: [],
  decisionHistory: [],
  isLoading: true,
  error: null,
});

const createInitialStoreState = (): GameStoreState => ({
  gameState: createInitialGameState(),
  endingSummary: '',
  consequences: undefined,
  maxDecisions: MAX_DECISIONS,
  streamText: '',
  showActionStrip: false,
  actionStripShown:
    sessionStorage.getItem(SESSION_KEYS.ACTION_STRIP_SHOWN) === 'true',
  donationCampaign: null,
});

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Apply impact to game parameters with NaN protection
 */
export function applyImpact(
  state: Pick<
    GameState,
    'militaryStrength' | 'economy' | 'resilience' | 'westernSupport' | 'trust'
  >,
  impact: Option['impact'],
): Pick<
  GameState,
  'militaryStrength' | 'economy' | 'resilience' | 'westernSupport' | 'trust'
> {
  const safeAdd = (current: number, delta: number | undefined): number => {
    const safeCurrent = Number.isNaN(current) ? 50 : current;
    const safeDelta = Number.isNaN(delta) || delta === undefined ? 0 : delta;
    return safeCurrent + safeDelta;
  };

  const clamp = (value: number): number => {
    if (Number.isNaN(value)) {
      console.error(
        '[gameStore] NaN detected in stats calculation, using default value',
      );
      return 50;
    }
    return Math.max(0, Math.min(100, value));
  };

  return {
    militaryStrength: clamp(
      safeAdd(state.militaryStrength, impact.militaryStrength),
    ),
    economy: clamp(safeAdd(state.economy, impact.economy)),
    resilience: clamp(safeAdd(state.resilience, impact.resilience)),
    westernSupport: clamp(safeAdd(state.westernSupport, impact.westernSupport)),
    trust: clamp(safeAdd(state.trust, impact.trust)),
  };
}

// =============================================================================
// STORE
// =============================================================================

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ...createInitialStoreState(),

      // =========================================================================
      // GAME INITIALIZATION
      // =========================================================================

      initializeGame: (scenario, config) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            currentScenario: scenario,
            ...config.initialParameters,
            isLoading: false,
            error: null,
          },
          maxDecisions: config.maxDecisions,
        }));
      },

      // =========================================================================
      // GAME STATE MUTATIONS
      // =========================================================================

      updateParameters: (params) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            ...params,
          },
        }));
      },

      setCurrentScenario: (scenario) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            currentScenario: scenario,
          },
        }));
      },

      incrementDecision: () => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            decisionNumber: state.gameState.decisionNumber + 1,
          },
        }));
      },

      setPhase: (phase) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            phase,
          },
        }));
      },

      setGameOver: (type) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            phase: 'gameOver',
            gameOverType: type,
            isLoading: false,
          },
        }));
      },

      setLoading: (isLoading) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            isLoading,
          },
        }));
      },

      setError: (error) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            error,
            isLoading: false,
          },
        }));
      },

      // =========================================================================
      // DECISION HANDLING
      // =========================================================================

      addDecisionRecord: (record) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            decisionHistory: [...state.gameState.decisionHistory, record],
          },
        }));
      },

      addToConversationHistory: (messages) => {
        set((state) => ({
          gameState: {
            ...state.gameState,
            conversationHistory: [
              ...state.gameState.conversationHistory,
              ...messages,
            ],
          },
        }));
      },

      // =========================================================================
      // STREAMING
      // =========================================================================

      setStreamText: (text) => set({ streamText: text }),

      appendStreamText: (delta) =>
        set((state) => ({
          streamText: state.streamText + delta,
        })),

      setEndingSummary: (summary) => set({ endingSummary: summary }),

      appendEndingSummary: (delta) =>
        set((state) => ({
          endingSummary: state.endingSummary + delta,
        })),

      setConsequences: (consequences) => set({ consequences }),

      // =========================================================================
      // ACTION STRIP
      // =========================================================================

      setShowActionStrip: (show) => set({ showActionStrip: show }),

      markActionStripShown: () => {
        sessionStorage.setItem(SESSION_KEYS.ACTION_STRIP_SHOWN, 'true');
        set({ actionStripShown: true, showActionStrip: false });
      },

      // =========================================================================
      // CAMPAIGN
      // =========================================================================

      setDonationCampaign: (campaign) => {
        set({ donationCampaign: campaign });
        if (campaign) {
          localStorage.setItem(
            STORAGE_KEYS.DONATION_CAMPAIGN,
            JSON.stringify(campaign),
          );
        } else {
          localStorage.removeItem(STORAGE_KEYS.DONATION_CAMPAIGN);
        }
      },

      // =========================================================================
      // RESET
      // =========================================================================

      resetGame: () => {
        const currentCampaign = get().donationCampaign;
        set({
          ...createInitialStoreState(),
          donationCampaign: currentCampaign,
          actionStripShown:
            sessionStorage.getItem(SESSION_KEYS.ACTION_STRIP_SHOWN) === 'true',
        });
      },

      clearProgress: () => {
        localStorage.removeItem(STORAGE_KEYS.GAME_PROGRESS);
        set({
          gameState: createInitialGameState(),
          endingSummary: '',
          consequences: undefined,
          streamText: '',
        });
      },
    }),
    {
      name: STORAGE_KEYS.GAME_PROGRESS,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist game-relevant state, not UI state
        gameState: state.gameState.phase === 'playing' ? state.gameState : null,
        consequences:
          state.gameState.phase === 'playing' ? state.consequences : undefined,
        maxDecisions: state.maxDecisions,
        // Don't persist: streamText, showActionStrip, endingSummary (transient)
      }),
      // Don't rehydrate if game is not in playing phase
      onRehydrateStorage: () => (state) => {
        if (state && !state.gameState) {
          // If no saved game state, create fresh
          state.gameState = createInitialGameState();
        }
      },
    },
  ),
);

// =============================================================================
// SELECTORS (for optimized re-renders)
// =============================================================================

export const selectGameState = (state: GameStore) => state.gameState;
export const selectPhase = (state: GameStore) => state.gameState.phase;
export const selectDecisionNumber = (state: GameStore) =>
  state.gameState.decisionNumber;
export const selectCurrentScenario = (state: GameStore) =>
  state.gameState.currentScenario;
export const selectIsLoading = (state: GameStore) => state.gameState.isLoading;
export const selectError = (state: GameStore) => state.gameState.error;
export const selectParameters = (state: GameStore) => ({
  militaryStrength: state.gameState.militaryStrength,
  economy: state.gameState.economy,
  resilience: state.gameState.resilience,
  westernSupport: state.gameState.westernSupport,
  trust: state.gameState.trust,
});
export const selectDonationCampaign = (state: GameStore) =>
  state.donationCampaign;
export const selectShowActionStrip = (state: GameStore) =>
  state.showActionStrip;
export const selectStreamText = (state: GameStore) => state.streamText;
export const selectEndingSummary = (state: GameStore) => state.endingSummary;
export const selectConsequences = (state: GameStore) => state.consequences;
export const selectMaxDecisions = (state: GameStore) => state.maxDecisions;

// =============================================================================
// EXPORTS
// =============================================================================
